import {
  VoiceService,
  VoiceConnection,
  VoiceDevice,
  VoiceServiceError,
  VoiceServiceEvents,
  VoiceCallOptions,
  VoiceConnectionStatus,
  AudioDevices
} from './types'

/**
 * Mock Voice Service Implementation
 *
 * This class provides a mock implementation of the VoiceService interface
 * for testing and development purposes. It simulates call behavior without
 * making actual phone calls.
 */
export class MockVoiceService implements VoiceService {
  private device: VoiceDevice | null = null
  private activeConnection: VoiceConnection | null = null
  private events: Partial<VoiceServiceEvents> = {}
  private connectionStatus: VoiceConnectionStatus = 'idle'
  // Use ReturnType<typeof setTimeout> so this works in both browser and Node environments
  private callSimulationTimer: ReturnType<typeof setTimeout> | null = null
  private muted: boolean = false
  private selectedInputDevice: string = 'default-input'
  private selectedOutputDevice: string = 'default-output'

  async initialize(_accessToken: string, events?: Partial<VoiceServiceEvents>): Promise<VoiceDevice> {
    this.events = events || {}

    try {
      // Simulate device initialization
      await this.simulateDelay(1000) // 1 second delay

      this.device = {
        deviceId: 'mock-device-' + Math.random().toString(36).substr(2, 9),
        isReady: true,
        isRegistered: true
      }

      // Trigger device ready event
      this.events.onDeviceReady?.(this.device)

      return this.device
    } catch (error) {
      const serviceError: VoiceServiceError = {
        code: 'MOCK_INIT_ERROR',
        message: 'Mock initialization failed',
        originalError: error
      }
      this.events.onDeviceError?.(serviceError)
      throw serviceError
    }
  }

  async makeCall(options: VoiceCallOptions): Promise<VoiceConnection> {
    if (!this.device?.isReady) {
      throw new Error('Mock device not ready')
    }

    try {
      // Create mock connection
      const connection: VoiceConnection = {
        connectionId: 'mock-call-' + Math.random().toString(36).substr(2, 9),
        phoneNumber: options.phoneNumber,
        status: 'connecting',
        startTime: new Date()
      }

      this.activeConnection = connection
      this.connectionStatus = 'connecting'

      // Notify call started
      this.events.onCallStarted?.(connection)
      this.events.onConnectionStatusChange?.(connection)

      // Simulate call progression
      this.simulateCallProgression()

      return connection
    } catch (error) {
      const serviceError: VoiceServiceError = {
        code: 'MOCK_CALL_ERROR',
        message: 'Mock call failed',
        originalError: error
      }
      throw serviceError
    }
  }

  async endCall(): Promise<void> {
    if (!this.activeConnection) {
      throw new Error('No active call to end')
    }

    try {
      // Clear simulation timer
      if (this.callSimulationTimer) {
        clearTimeout(this.callSimulationTimer)
        this.callSimulationTimer = null
      }

      // Update connection
      const endedConnection: VoiceConnection = {
        ...this.activeConnection,
        status: 'disconnected',
        endTime: new Date(),
        duration: this.activeConnection.startTime
          ? Math.floor((new Date().getTime() - this.activeConnection.startTime.getTime()) / 1000)
          : 0
      }

      this.events.onCallEnded?.(endedConnection)
      this.events.onConnectionStatusChange?.(endedConnection)

      // Reset state
      this.activeConnection = null
      this.connectionStatus = 'idle'
    } catch (error) {
      const serviceError: VoiceServiceError = {
        code: 'MOCK_END_CALL_ERROR',
        message: 'Mock end call failed',
        originalError: error
      }
      throw serviceError
    }
  }

  async sendDTMF(digit: string): Promise<void> {
    if (!this.activeConnection) {
      throw new Error('No active call to send DTMF')
    }

    // Simulate DTMF sending - just log it
    console.log(`Mock DTMF sent: ${digit}`)

    // Simulate brief delay
    await this.simulateDelay(100)
  }

  getConnectionStatus(): VoiceConnectionStatus {
    return this.connectionStatus
  }

  getActiveConnection(): VoiceConnection | null {
    return this.activeConnection
  }

  isReady(): boolean {
    return this.device?.isReady ?? false
  }

  getAudioDevices(): AudioDevices {
    // Return mock audio devices
    return {
      inputs: [
        { deviceId: 'default-input', label: 'Default Microphone' },
        { deviceId: 'mock-mic-1', label: 'External Microphone' },
      ],
      outputs: [
        { deviceId: 'default-output', label: 'Default Speaker' },
        { deviceId: 'mock-speaker-1', label: 'Headphones' },
      ],
      selectedInput: this.selectedInputDevice,
      selectedOutput: this.selectedOutputDevice,
    }
  }

  async setInputDevice(deviceId: string): Promise<void> {
    console.log(`Mock: Setting input device to ${deviceId}`)
    this.selectedInputDevice = deviceId
  }

  async setOutputDevice(deviceId: string): Promise<void> {
    console.log(`Mock: Setting output device to ${deviceId}`)
    this.selectedOutputDevice = deviceId
  }

  setMuted(muted: boolean): void {
    console.log(`Mock: Setting muted to ${muted}`)
    this.muted = muted
  }

  isMuted(): boolean {
    return this.muted
  }

  updateToken(_accessToken: string): void {
    // Mock implementation - no actual token update needed
    console.log('Mock: Token updated (no-op)')
  }

  simulateWarning(warningName: string): void {
    this.events.onWarning?.(warningName)
  }

  clearWarning(warningName: string): void {
    this.events.onWarningCleared?.(warningName)
  }

  destroy(): void {
    // Clear any timers
    if (this.callSimulationTimer) {
      clearTimeout(this.callSimulationTimer)
      this.callSimulationTimer = null
    }

    // Reset state
    this.device = null
    this.activeConnection = null
    this.connectionStatus = 'idle'
    this.events = {}
  }

  /**
   * Private helper methods
   */

  private async simulateDelay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private simulateCallProgression(): void {
    if (!this.activeConnection) return

    // Step 1: Connecting -> Ringing (after 500ms)
    this.callSimulationTimer = setTimeout(() => {
      if (!this.activeConnection) return

      const ringingConnection: VoiceConnection = {
        ...this.activeConnection,
        status: 'ringing'
      }

      this.activeConnection = ringingConnection
      this.connectionStatus = 'ringing'
      this.events.onConnectionStatusChange?.(ringingConnection)

      // Step 2: Ringing -> Connected (after another 2 seconds)
      this.callSimulationTimer = setTimeout(() => {
        if (!this.activeConnection) return

        const connectedConnection: VoiceConnection = {
          ...this.activeConnection,
          status: 'connected'
        }

        this.activeConnection = connectedConnection
        this.connectionStatus = 'connected'
        this.events.onConnectionStatusChange?.(connectedConnection)

        // Start duration tracking
        this.startDurationTracking()

      }, 2000) // 2 second ring time

    }, 500) // 500ms to start ringing
  }

  private startDurationTracking(): void {
    if (!this.activeConnection || this.activeConnection.status !== 'connected') {
      return
    }

    // Update duration every second
    const updateDuration = () => {
      if (!this.activeConnection || this.activeConnection.status !== 'connected') {
        return
      }

      const duration = this.activeConnection.startTime
        ? Math.floor((new Date().getTime() - this.activeConnection.startTime.getTime()) / 1000)
        : 0

      const updatedConnection: VoiceConnection = {
        ...this.activeConnection,
        duration
      }

      this.activeConnection = updatedConnection
      this.events.onConnectionStatusChange?.(updatedConnection)

      // Schedule next update
      this.callSimulationTimer = setTimeout(updateDuration, 1000)
    }

    // Start the tracking
    this.callSimulationTimer = setTimeout(updateDuration, 1000)
  }
}
